import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/* eslint-disable import/order */
/* eslint arrow-parens: 0 */
/* eslint implicit-arrow-linebreak: 0 */

/**
 * Creating this factory separate from the dialog factory intentionally.
 * It is still not clear whether we would be showing all types of filters in the bar.
 */
import { defaultMemoize } from 'reselect';
import _keyBy from 'lodash/keyBy';
import _map from 'lodash/map';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { toMoment } from '@tekion/tekion-base/utils/dateUtils';
import cx from 'classnames';

// Constants
import { EMPTY_OBJECT } from '@tekion/tekion-base/app.constants';
import FILTER_TYPES from '../../../../constants/filterSection.filterTypes';
import WORKSPACE_FILTER_TYPES from '../../../../constants/filterSection.workspaceFilterTypes';

// Components
import { Select, AdvanceAsyncSelect } from '../../../../../../molecules/advancedSelect';
import { MultiSelect } from '../../../../../../molecules/multiSelect';
import VirtualizedMultiSelect, { VirtualizedMultiAsync } from '../../../../../../molecules/virtualizedMultiSelect';
import VirtualizedSelect from '../../../../../../molecules/virtualizedSelect';
import DateRangeWithoutPresets from '../../../../../../molecules/dateRangeWithoutPresets';
import DateTimeRangeWithoutPresets from '../../../../molecules/DateTimeRange';
import DateInput, { MonthPicker } from '../../../../../../atoms/DateInput';
import withSelectAllOptions from '../../../../../../hoc/withSelectAllOptions';
import withDateAsFilter from '../../../../containers/withDateAsFilter';
// Readers
import filterTypeReader from '../../../../readers/FilterType';
import { getDatePicker } from '../../../../utils/filterSection.utils';

// Helpers
import { getOptionsFromFilter, shouldShowSelectAll } from '../../../../helpers/filterSection.filterType';

// Styles
import s from '../filter.module.scss';
var MultiSelectWithAll = withSelectAllOptions(MultiSelect);
var VALID_PROPS_TO_PASS = {
  SINGLE_SELECT: ['isDisabled', 'filterOption', 'shouldSortOptions', 'sort'],
  ASYNC_SINGLE_SELECT: ['filterOption', 'sort', 'filters', 'lookUpSearchApi', 'lookupByKeysApi', 'shouldFetchOnValueChange', 'shouldFetchInitialData', 'shouldHideOverlayIfNotVisible'],
  MULTI_SELECT: ['filterOption', 'sort', 'shouldSortOptions', 'formatOptionLabel'],
  ASYNC_MULTI_SELECT: ['filterOption', 'sort', 'filters', 'lookUpSearchApi', 'lookupByKeysApi', 'isDisabled', 'shouldFetchOnValueChange', 'shouldFetchInitialData', 'shouldHideOverlayIfNotVisible'],
  DATE_PICKER: ['disabledDate', 'allowClear'],
  MONTH_PICKER: ['align', 'allowClear', 'disabled', 'disabledDate', 'format'],
  DATE_WITHOUT_PRESET: ['disabledDate', 'getCalendarContainer', 'allowClear', 'disabled'],
  DATE_TIME_WITHOUT_PRESET: ['disabledDate', 'getCalendarContainer', 'allowClear', 'disabled', 'showTime', 'format', 'containerClassName', 'className'],
  WORKSPACE_ASYNC_MULTI_SELECT: ['filterOption', 'sort', 'filters', 'dealerIds', 'shouldFetchOnValueChange']
};
var SELECT_STYLE_OVERRIDES = {
  control: function control(styles, _ref) {
    var isFocused = _ref.isFocused;
    if (!isFocused) {
      return {
        borderColor: 'transparent'
      };
    }
    return '';
  },
  menuPortal: function menuPortal(style) {
    return _objectSpread(_objectSpread({}, style), {}, {
      zIndex: 9999
    });
  }
};
var getSingleSelectProps = function getSingleSelectProps(filterType, value) {
  var baseProps = _objectSpread({
    options: getOptionsFromFilter(filterType),
    className: cx(s.select, _get(filterType, 'additional.className')),
    value: value,
    menuPortalTarget: document.body,
    styleOverrides: SELECT_STYLE_OVERRIDES
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.SINGLE_SELECT));
  return {
    component: Select,
    baseProps: baseProps
  };
};
var getSingleDatePickerProps = function getSingleDatePickerProps(_ref2, value) {
  var _ref2$additional = _ref2.additional,
    additional = _ref2$additional === void 0 ? EMPTY_OBJECT : _ref2$additional;
  return _objectSpread({
    value: toMoment(value)
  }, _pick(additional, VALID_PROPS_TO_PASS.DATE_PICKER));
};
var advancedAsyncSelectProps = function advancedAsyncSelectProps(filterType, value) {
  var resourceType = filterType.resourceType,
    key = filterType.key,
    resourceParams = filterType.resourceParams;
  var baseProps = _objectSpread(_objectSpread({
    resourceType: resourceType,
    key: key,
    className: s.select,
    value: value,
    styles: SELECT_STYLE_OVERRIDES,
    resourceParams: resourceParams
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.ASYNC_SINGLE_SELECT)), {}, {
    shouldFetchOnValueChange: true
  });
  return {
    component: AdvanceAsyncSelect,
    baseProps: baseProps
  };
};
var advancedAsyncMultiSelectProps = function advancedAsyncMultiSelectProps(filterType, value) {
  var resourceType = filterType.resourceType,
    key = filterType.key,
    resourceParams = filterType.resourceParams;
  var baseProps = _objectSpread({
    resourceType: resourceType,
    key: key,
    className: s.select,
    value: value,
    styles: SELECT_STYLE_OVERRIDES,
    resourceParams: resourceParams
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.ASYNC_MULTI_SELECT));
  return {
    component: VirtualizedMultiAsync,
    baseProps: baseProps
  };
};
var getSelectedOptionLabels = defaultMemoize(function (options, values) {
  var optionsMapByValue = _keyBy(options, 'value');
  return _map(values, function (selectedValue) {
    return _get(optionsMapByValue, "".concat(selectedValue, ".label"));
  });
});
var getMultiSelectProps = function getMultiSelectProps(filterType, values) {
  var options = getOptionsFromFilter(filterType);
  var showSelectAll = shouldShowSelectAll(filterType);
  var baseProps = _objectSpread({
    options: options,
    className: s.select,
    values: values,
    selectedValueLabels: getSelectedOptionLabels(options, values)
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.MULTI_SELECT));
  return {
    component: showSelectAll ? MultiSelectWithAll : MultiSelect,
    baseProps: baseProps
  };
};
var getDateWithoutPresetPickerProps = function getDateWithoutPresetPickerProps(_ref3, values) {
  var _ref3$additional = _ref3.additional,
    additional = _ref3$additional === void 0 ? EMPTY_OBJECT : _ref3$additional;
  return _objectSpread({
    values: values
  }, _pick(additional, VALID_PROPS_TO_PASS.DATE_WITHOUT_PRESET));
};
var getDateWithoutPresetPicker = function getDateWithoutPresetPicker(filterType, values) {
  var baseProps = getDateWithoutPresetPickerProps(filterType, values);
  return {
    component: withDateAsFilter(DateRangeWithoutPresets),
    baseProps: baseProps
  };
};
var getDateTimeWithoutPresetPickerProps = function getDateTimeWithoutPresetPickerProps(_ref4, values) {
  var _ref4$additional = _ref4.additional,
    additional = _ref4$additional === void 0 ? EMPTY_OBJECT : _ref4$additional;
  return _objectSpread({
    values: values
  }, _pick(additional, VALID_PROPS_TO_PASS.DATE_TIME_WITHOUT_PRESET));
};
var getDateTimeWithoutPresetPicker = function getDateTimeWithoutPresetPicker(filterType, values) {
  var baseProps = getDateTimeWithoutPresetPickerProps(filterType, values);
  return {
    component: withDateAsFilter(DateTimeRangeWithoutPresets),
    baseProps: baseProps
  };
};
var getSingleDatePicker = function getSingleDatePicker(filterType, value) {
  var filterValue = value || _get(filterType, 'additional.defaultValue');
  var baseProps = getSingleDatePickerProps(filterType, filterValue);
  return {
    component: DateInput,
    baseProps: baseProps
  };
};
var getBasePropsForVirtualizedSelect = function getBasePropsForVirtualizedSelect(filterType, values) {
  var options = getOptionsFromFilter(filterType);
  return _objectSpread({
    options: options,
    className: s.select,
    values: values,
    selectedValueLabels: getSelectedOptionLabels(options, values)
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.SINGLE_SELECT));
};
var getVirtualizedMultiSelectProps = function getVirtualizedMultiSelectProps(filterType, values) {
  var baseProps = getBasePropsForVirtualizedSelect(filterType, values);
  return {
    component: VirtualizedMultiSelect,
    baseProps: baseProps
  };
};
var getVirtualizedSelectProps = function getVirtualizedSelectProps(filterType, values) {
  var baseProps = getBasePropsForVirtualizedSelect(filterType, values);
  return {
    component: VirtualizedSelect,
    baseProps: _objectSpread(_objectSpread({}, baseProps), {}, {
      value: values
    })
  };
};
var getBasePropsForMonthPicker = function getBasePropsForMonthPicker() {
  var filterParams = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var values = arguments.length > 1 ? arguments[1] : undefined;
  var baseProps = _objectSpread({
    className: s.monthPicker,
    value: values
  }, _pick(filterParams.additional, VALID_PROPS_TO_PASS.MONTH_PICKER));
  return baseProps;
};
var getMonthPicker = function getMonthPicker(filterParams, values) {
  var _filterParams$additio;
  var baseProps = getBasePropsForMonthPicker(filterParams, values);
  var MonthPickerComponent = ((_filterParams$additio = filterParams.additional) === null || _filterParams$additio === void 0 ? void 0 : _filterParams$additio.component) || MonthPicker;
  return {
    component: MonthPickerComponent,
    baseProps: baseProps
  };
};
var getCustomFilterProps = function getCustomFilterProps(filterParams, values) {
  var _objectSpread2;
  var _ref5 = filterParams || EMPTY_OBJECT,
    key = _ref5.key,
    additional = _ref5.additional;
  var _ref6 = additional || EMPTY_OBJECT,
    valueKey = _ref6.valueKey,
    className = _ref6.className,
    stylesOverride = _ref6.stylesOverride,
    component = _ref6.component,
    renderProps = _ref6.renderProps;
  return {
    component: component,
    baseProps: _objectSpread(_objectSpread({}, renderProps), {}, (_objectSpread2 = {
      additional: additional,
      key: key,
      styles: stylesOverride
    }, _defineProperty(_objectSpread2, valueKey, values), _defineProperty(_objectSpread2, "className", className), _objectSpread2))
  };
};

// Centralised
var advancedWorkspaceAsyncMultiSelectProps = function advancedWorkspaceAsyncMultiSelectProps(filterType, value) {
  var resourceType = filterType.resourceType,
    key = filterType.key,
    resourceParams = filterType.resourceParams,
    FilterComponent = filterType.filterComponent;
  var baseProps = _objectSpread({
    resourceType: resourceType,
    key: key,
    className: s.select,
    value: value,
    styles: SELECT_STYLE_OVERRIDES,
    resourceParams: resourceParams
  }, _pick(filterType.additional, VALID_PROPS_TO_PASS.WORKSPACE_ASYNC_MULTI_SELECT));
  return {
    component: FilterComponent,
    baseProps: baseProps
  };
};
var getRenderConfig = function getRenderConfig() {
  var filterType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var values = arguments.length > 1 ? arguments[1] : undefined;
  var additional = arguments.length > 2 ? arguments[2] : undefined;
  var typeOfFilter = filterTypeReader.type(filterType);
  if (typeOfFilter === FILTER_TYPES.CUSTOM) {
    return getCustomFilterProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.SINGLE_SELECT) {
    return getSingleSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.ASYNC_SINGLE_SELECT) {
    return advancedAsyncSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.ASYNC_MULTI_SELECT) {
    return advancedAsyncMultiSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.MULTI_SELECT) {
    return getMultiSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.DATE) {
    return getDatePicker(filterType, values, additional);
  }
  if (typeOfFilter === FILTER_TYPES.DATE_WITHOUT_PRESET) {
    return getDateWithoutPresetPicker(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.DATE_TIME_WITHOUT_PRESET) {
    return getDateTimeWithoutPresetPicker(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.DATE_PICKER) {
    return getSingleDatePicker(filterType, _get(values, '0'));
  }
  if (typeOfFilter === FILTER_TYPES.VIRTUALIZED_MULTI_SELECT) {
    return getVirtualizedMultiSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.VIRTUALIZED_SELECT) {
    return getVirtualizedSelectProps(filterType, values);
  }
  if (typeOfFilter === FILTER_TYPES.MONTH_PICKER) {
    return getMonthPicker(filterType, values);
  }

  // Centralised
  if (typeOfFilter === WORKSPACE_FILTER_TYPES.WORKSPACE_ASYNC_MULTI_SELECT) {
    return advancedWorkspaceAsyncMultiSelectProps(filterType, values);
  }
  return getSingleSelectProps(filterType);
};
export default getRenderConfig;